import { useTranslation } from 'react-i18next';
import type { BreakdownDto } from '@kreart/shared';
import { usePricingConstants } from '../hooks';
import { formatMarkup } from '../specialMaterialFlags';

interface Props {
  breakdown: BreakdownDto | null | undefined;
  loading?: boolean;
  error?: unknown;
}

/**
 * Renders a pricing-engine breakdown: per-component lines plus subtotal, the
 * markup each one carries, and the final totalNet. Used as a live preview
 * inside Add*Dialogs and detail panes.
 */
export function BreakdownPanel({ breakdown, loading, error }: Props) {
  const { t } = useTranslation();
  // Only fetched once the markup is actually on screen; cached for the session.
  const { data: constants } = usePricingConstants({
    enabled: Boolean(breakdown?.glassSandingApplied),
  });

  if (loading) {
    return (
      <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
        {t('pricing.previewLoading') ?? 'Calculating…'}
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive"
      >
        {t('pricing.previewError') ?? 'Could not preview pricing.'}
      </div>
    );
  }

  if (!breakdown) {
    return (
      <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
        {t('pricing.previewEmpty') ?? 'Pick material, edge, and dimensions to preview.'}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border bg-muted/20 p-3 text-xs">
      <div className="mb-2 font-medium text-foreground">
        {t('pricing.breakdownTitle') ?? 'Pricing breakdown'}
      </div>
      <ul className="space-y-1">
        {breakdown.components.map((c, idx) => (
          <li key={`${c.kind}-${idx}`} className="flex justify-between gap-2">
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {c.label}
              {/* Per line, because there is no single row markup left to show
                  once every board carries its own. The amount beside it already
                  includes this. */}
              {c.coefficient && parseFloat(c.coefficient) !== 1 && (
                <span className="ml-1 text-[10px] tabular-nums opacity-70">×{c.coefficient}</span>
              )}
            </span>
            <span className="tabular-nums">{formatNum(c.amount)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-2 border-t border-border pt-2 space-y-1">
        <Row
          label={t('pricing.subtotalNet') ?? 'Subtotal'}
          value={formatNum(breakdown.subtotalNet)}
        />
        {/* Only elements and lighting still have one markup for the whole row;
            everything else reports it per component above. */}
        {breakdown.coefficientApplied && parseFloat(breakdown.coefficientApplied) !== 1 && (
          <Row
            label={t('pricing.coefficient') ?? 'Coefficient'}
            value={`× ${breakdown.coefficientApplied}`}
          />
        )}
        {breakdown.glassSandingApplied && (
          <Row
            label={t('pricing.glassSanding')}
            value={formatMarkup(constants?.glassSandingMarkup, t)}
          />
        )}
        <Row
          label={t('pricing.totalNet') ?? 'Total net'}
          value={`${formatNum(breakdown.totalNet)} ${breakdown.currency}`}
          strong
        />
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  strong,
  muted,
}: {
  label: string;
  value: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={`flex justify-between ${strong ? 'font-semibold text-foreground' : ''} ${
        muted ? 'pl-3 text-muted-foreground' : ''
      }`}
    >
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function formatNum(s: string): string {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n.toFixed(2) : s;
}
